import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ErroApi } from '../api';
import { useLogin } from './sessao';
import { SeletorIdioma } from '../layout/Preferencias';
import { MarcaCortex } from '../layout/Marca';

const campo = 'h-11 rounded-lg border border-borda bg-sup-1 px-3 text-base text-tinta-1 outline-none focus:border-acento';
const botao = 'h-11 rounded-lg bg-acento font-medium text-acento-tinta transition-colors hover:bg-acento-forte disabled:opacity-50';
const linkSecundario = 'self-start text-sm text-tinta-3 underline-offset-4 hover:text-tinta-1 hover:underline';

/** F4: duas colunas, marca à esquerda e formulário à direita. Moldura das três telas públicas. */
function PaginaPublica({ kicker, titulo, children }: { kicker: string; titulo: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="grid altura-janela lg:grid-cols-2">
      <section className="hidden flex-col justify-between border-r border-borda bg-sup-1 p-12 lg:flex">
        <MarcaCortex grande />
        <div className="max-w-md">
          <p className="kicker mb-4">Grupo ARX</p>
          <p className="text-4xl leading-tight font-semibold tracking-tight">{t('marca.linha')}</p>
        </div>
        <p className="kicker">v1 · fase 1</p>
      </section>

      <section className="flex flex-col p-6 sm:p-12">
        <div className="flex justify-end">
          <SeletorIdioma />
        </div>
        <div className="mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
          <div className="mb-8 lg:hidden">
            <MarcaCortex grande />
          </div>
          <p className="kicker mb-2">{kicker}</p>
          <h1 className="mb-8 text-3xl font-semibold tracking-tight">{titulo}</h1>
          {children}
        </div>
      </section>
    </div>
  );
}

function Alerta({ tom, children }: { tom: 'perigo' | 'sucesso'; children: ReactNode }) {
  const cores = tom === 'perigo' ? 'border-perigo/40 bg-perigo/10 text-perigo' : 'border-sucesso/40 bg-sucesso/10 text-sucesso';
  return (
    <p role={tom === 'perigo' ? 'alert' : 'status'} className={`rounded-lg border px-3 py-2 text-sm ${cores}`}>
      {children}
    </p>
  );
}

// SPEC-BASE-03: limite e serviço fora têm texto próprio nas telas públicas; o resto continua "rede".
const COMUNS = ['limite_excedido', 'servico_indisponivel'];
function textoPublico(t: (k: string) => string, err: unknown, padrao: string): string {
  return err instanceof ErroApi && COMUNS.includes(err.code) ? t(`nucleo:erros.${err.code}`) : t(padrao);
}

export function Login() {
  const { t } = useTranslation();
  const login = useLogin();
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    login.mutate({ email, senha });
  };

  const erro = login.error instanceof ErroApi ? login.error.code : login.error ? 'rede' : null;
  const chaveErro = erro && ['credenciais_invalidas', 'bloqueado', 'sem_workspace', 'rede'].includes(erro) ? erro : 'rede';

  return (
    <PaginaPublica kicker={t('login.kicker')} titulo={t('login.titulo')}>
      <form onSubmit={enviar} className="flex flex-col gap-5" noValidate>
        <label className="flex flex-col gap-2 text-sm text-tinta-2">
          {t('login.email')}
          <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className={campo} />
        </label>
        <label className="flex flex-col gap-2 text-sm text-tinta-2">
          {t('login.senha')}
          <input type="password" autoComplete="current-password" required value={senha} onChange={(e) => setSenha(e.target.value)} className={campo} />
        </label>
        {erro && <Alerta tom="perigo">{erro === 'rede' || chaveErro !== 'rede' ? t(`login.erro.${chaveErro}`) : textoPublico(t, login.error, 'login.erro.rede')}</Alerta>}
        <button type="submit" disabled={login.isPending || !email || !senha} className={botao}>
          {login.isPending ? t('login.entrando') : t('login.entrar')}
        </button>
        <a href="/recuperar" className={linkSecundario}>
          {t('login.esqueci')}
        </a>
      </form>
    </PaginaPublica>
  );
}

/** N2: pede o link de recuperação. A resposta é a mesma exista ou não o e-mail (sem enumeração). */
export function Recuperar() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [estado, setEstado] = useState<'livre' | 'enviando' | 'enviado' | 'erro'>('livre');
  const [falha, setFalha] = useState<unknown>(null);

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setEstado('enviando');
    try {
      await api<void>('/api/v1/auth/recuperar', { method: 'POST', json: { email } });
      setEstado('enviado');
    } catch (err) {
      setFalha(err);
      setEstado('erro');
    }
  };

  return (
    <PaginaPublica kicker={t('recuperar.kicker')} titulo={t('recuperar.titulo')}>
      {estado === 'enviado' ? (
        <div className="flex flex-col gap-5">
          <Alerta tom="sucesso">{t('recuperar.enviado')}</Alerta>
          <a href="/" className={linkSecundario}>
            {t('recuperar.voltar')}
          </a>
        </div>
      ) : (
        <form onSubmit={enviar} className="flex flex-col gap-5" noValidate>
          <p className="text-sm text-tinta-2">{t('recuperar.texto')}</p>
          <label className="flex flex-col gap-2 text-sm text-tinta-2">
            {t('login.email')}
            <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} className={campo} />
          </label>
          {estado === 'erro' && <Alerta tom="perigo">{textoPublico(t, falha, 'login.erro.rede')}</Alerta>}
          <button type="submit" disabled={estado === 'enviando' || !/^\S+@\S+\.\S+$/.test(email)} className={botao}>
            {t('recuperar.enviar')}
          </button>
          <a href="/" className={linkSecundario}>
            {t('recuperar.voltar')}
          </a>
        </form>
      )}
    </PaginaPublica>
  );
}

const SENHA_MINIMA = 10;

/** N1 e N2: define a senha pelo link de uso único do convite ou da recuperação (token no fragmento da URL). */
export function DefinirSenha() {
  const { t } = useTranslation();
  const [token] = useState(() => window.location.hash.replace(/^#/, ''));
  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [estado, setEstado] = useState<'livre' | 'enviando' | 'pronto' | 'invalido' | 'erro'>(() => (token.length >= 20 ? 'livre' : 'invalido'));

  const [falha, setFalha] = useState<unknown>(null);
  const curta = senha.length > 0 && senha.length < SENHA_MINIMA;
  const diferente = confirma.length > 0 && senha !== confirma;

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setEstado('enviando');
    try {
      await api<void>('/api/v1/auth/definir-senha', { method: 'POST', json: { token, senha } });
      // O token sai da barra de endereço depois de usado.
      window.history.replaceState(null, '', '/definir-senha');
      setEstado('pronto');
    } catch (err) {
      setFalha(err);
      setEstado(err instanceof ErroApi && err.code === 'token_invalido' ? 'invalido' : 'erro');
    }
  };

  return (
    <PaginaPublica kicker={t('definirSenha.kicker')} titulo={t('definirSenha.titulo')}>
      {estado === 'pronto' ? (
        <div className="flex flex-col gap-5">
          <Alerta tom="sucesso">{t('definirSenha.pronto')}</Alerta>
          <a href="/" className={botao + ' grid place-items-center'}>
            {t('login.entrar')}
          </a>
        </div>
      ) : estado === 'invalido' ? (
        <div className="flex flex-col gap-5">
          <Alerta tom="perigo">{t('definirSenha.invalido')}</Alerta>
          <a href="/recuperar" className={linkSecundario}>
            {t('definirSenha.pedirNovo')}
          </a>
        </div>
      ) : (
        <form onSubmit={enviar} className="flex flex-col gap-5" noValidate>
          <label className="flex flex-col gap-2 text-sm text-tinta-2">
            {t('definirSenha.nova')}
            <input type="password" autoComplete="new-password" required minLength={SENHA_MINIMA} value={senha} onChange={(e) => setSenha(e.target.value)} className={campo} aria-describedby="regra-senha" />
            <span id="regra-senha" className={`text-xs ${curta ? 'text-perigo' : 'text-tinta-3'}`}>
              {t('definirSenha.regra', { n: SENHA_MINIMA })}
            </span>
          </label>
          <label className="flex flex-col gap-2 text-sm text-tinta-2">
            {t('definirSenha.confirmar')}
            <input type="password" autoComplete="new-password" required value={confirma} onChange={(e) => setConfirma(e.target.value)} className={campo} />
            {diferente && <span className="text-xs text-perigo">{t('definirSenha.diferente')}</span>}
          </label>
          {estado === 'erro' && <Alerta tom="perigo">{textoPublico(t, falha, 'login.erro.rede')}</Alerta>}
          <button type="submit" disabled={estado === 'enviando' || senha.length < SENHA_MINIMA || senha !== confirma} className={botao}>
            {t('definirSenha.salvar')}
          </button>
        </form>
      )}
    </PaginaPublica>
  );
}
